import { FunctionComponent, useMemo } from "react";
import {
  Box,
  Breadcrumbs,
  Typography,
  lighten,
  styled,
  useTheme,
} from "@mui/material";
import { pageMapping } from "../../builder/mapping/page";
import { BackButton } from "./BackButton";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { LinkWrapper } from "./anchor/LinkWrapper";
import { useTranslation } from "../../helpers/i18n";

export type NavigationType = "breadcrumb" | "backButton";
interface Props {
  textColor?: string;
  navigation: {
    type?: NavigationType;
    links: {
      label?: string;
      href?: string;
    }[];
  };
}

const NavigationContainer = styled(Box)(({ theme }) => ({
  paddingTop: 0,
  alignSelf: "flex-start",
  [theme.breakpoints.up("md")]: {
    padding: `1rem ${theme.defaultSpacing.md.page.edges} ${theme.defaultSpacing.md.page.edges} 0`,
    marginBottom: 0,
  },
}));

const StyledNextLink = styled(LinkWrapper)(() => ({
  textDecoration: "none",
  color: "inherit",
  textTransform: "uppercase",
  "&:hover": {
    cursor: "pointer",
    textDecoration: "underline",
  },
}));

const StyledTypography = styled(Typography)(() => ({
  textTransform: "uppercase",
}));

const PageNavigation: FunctionComponent<Props> = ({
  textColor,
  navigation,
}) => {
  const theme = useTheme();
  const { configuration } = useConfiguration();
  const { t } = useTranslation();
  const breadcrumbTextColor = lighten(theme.palette.text.default, 0.5);

  const content = useMemo(() => {
    if (navigation.type === "breadcrumb") {
      return (
        <Breadcrumbs aria-label={t("accessibility_breadcrumb")}>
          <StyledNextLink href={pageMapping.PLP.path}>
            <Typography color={breadcrumbTextColor} component="span">
              {configuration?.name}
            </Typography>
          </StyledNextLink>

          {navigation.links
            ?.filter(({ label }) => label)
            ?.map(({ label, href }, index) =>
              href ? (
                <StyledNextLink href={href}>
                  <Typography color={breadcrumbTextColor} component="span">
                    {label}
                  </Typography>
                </StyledNextLink>
              ) : (
                <StyledTypography color={breadcrumbTextColor} key={index}>
                  {label}
                </StyledTypography>
              ),
            )}
        </Breadcrumbs>
      );
    }

    const [first] = navigation.links || [];
    const { href } = first || {};

    return (
      <LinkWrapper href={href || pageMapping.PLP.path} passHref legacyBehavior>
        <BackButton textColor={textColor} />
      </LinkWrapper>
    );
  }, [navigation, breadcrumbTextColor, configuration?.name, textColor]);

  return <NavigationContainer>{content}</NavigationContainer>;
};

export default PageNavigation;
